import { map, find, fromPairs, size, groupBy, pickBy, first } from 'lodash-es'

export const useCatalog = defineStore('catalog', () => {
  const { $api } = useNuxtApp()
  const route = useRoute()

  const isLoading = ref(true)
  const isGlobalRegionForced = ref(false)
  const country = ref({ level: 0, details: { region: 'GLOBAL' } })
  const list = ref({})
  const upsell = ref([])
  const metadata = ref({})

  const upgradeData = reactive({
    data: [],
    metadata: {}
  })

  const runningPromotion = computed(() => {
    const items = Object.values(list.value)
    return {
      slug: first(map(items, 'promotion_slug')),
      name: first(map(items, 'promotion_name'))
    }
  })

  const hasUpsell = computed(() => !!upsell.value.length)
  const isPppActive = computed(() => country.value.level && !isGlobalRegionForced.value)

  const activeRegion = computed(() => {
    if (isGlobalRegionForced.value) return 'GLOBAL'

    return country.value.details.region
  })

  function parseCatalog (items, meta) {
    return fromPairs(map(items, (item) => {
      const bulletpoints = groupBy(item.bulletpoints?.map?.(bulletpoint => ({
        ...bulletpoint,
        ...find(meta.bulletpoints, { id: bulletpoint.id }, {})
      })), 'category')

      const groups = {
        DEFAULT: bulletpoints?.DEFAULT,
        AVAILABLE_NOW: bulletpoints?.AVAILABLE_NOW,
        COMING_SOON: bulletpoints?.COMING_SOON,
        COMING_IN_Q1: bulletpoints?.COMING_IN_Q1,
        COMING_IN_Q2: bulletpoints?.COMING_IN_Q2,
        COMING_IN_Q3: bulletpoints?.COMING_IN_Q3,
        COMING_IN_Q4: bulletpoints?.COMING_IN_Q4,
        COMING_IN_Q1_2024: bulletpoints?.COMING_IN_Q1_2024,
        COMING_IN_Q2_2024: bulletpoints?.COMING_IN_Q2_2024,
        COMING_IN_Q3_2024: bulletpoints?.COMING_IN_Q3_2024,
        COMING_IN_Q4_2024: bulletpoints?.COMING_IN_Q4_2024,
        COMING_IN_Q1_2025: bulletpoints?.COMING_IN_Q1_2025,
        IN_PLANNING: bulletpoints?.IN_PLANNING
      }

      return [item.sort_order, { ...item, bulletpoints: groups }]
    }))
  }

  async function getCountry () {
    try {
      const data = await $api.get('/country-code')
      country.value = data
    } catch (e) {
      console.info('Error getting country')
    }
  }

  async function getAll (params = {}, options = {}) {
    const defaults = {
      region: activeRegion.value,
      emulateDate: route.query.emulateDate,
      emulateHour: route.query.emulateHour
    }

    if (!size(list.value) || options.fresh) {
      isLoading.value = true
      const { data, meta } = await $api.get('/catalog', { ...defaults, ...params })
      isLoading.value = false

      metadata.value = meta
      list.value = parseCatalog(data, meta)
    }

    return list
  }

  async function fetchUpsell (slug, params = {}) {
    const { data } = await $api.get(`/upsell/${slug}`, {
      ...params,
      region: activeRegion.value
    })

    upsell.value = data.map((item) => ({ ...item, selected: false }))

    return data
  }

  async function getUpgradeableList (params, options = {}) {
    if (!size(upgradeData.data) || options.fresh) {
      const { data, meta } = await $api.get('/catalog', { upgrade: 1, includes: 'products' })

      upgradeData.metadata = meta
      upgradeData.data = data
    }

    return upgradeData.data
  }

  async function getOrderUpgradeList (uuid, options = {}) {
    const orderUpgradeList = {}
    const { data, meta } = await $api.get('/catalog', { order_id: uuid, includes: 'products' })

    orderUpgradeList.metadata = meta
    orderUpgradeList.data = data

    return orderUpgradeList
  }

  function parse (items) {
    return parseCatalog(items, upgradeData.metadata)
  }

  async function checkout ({ slug, type, region = null, quantity = 1, coupon = null, upsellItems = [] }) {
    const method = upsellItems.length ? 'POST' : 'GET'

    const { link } = await $api.request(method, '/checkout', {
      slug,
      quantity,
      type,
      upsell: upsellItems,
      region: region || activeRegion.value
    }, pickBy({
      'X-Coupon': coupon
    }))

    return link
  }

  async function upgrade ({ slug }) {
    const { link } = await $api.get('/upgrade', { slug })

    return link
  }

  function verifyPayment (id) {
    return $api.get(`/checkout/${id}`)
  }

  async function checkoutRetake (slug, params) {
    const { link } = await $api.get(`/certifications/${slug}/retake`, params)

    return link
  }

  function toggleRegionalPricing () {
    isGlobalRegionForced.value = !isGlobalRegionForced.value
    getAll({}, { fresh: true })
  }

  function clearRegionalPricing () {
    isGlobalRegionForced.value = false
  }

  function reset () {
    list.value = {}
    metadata.value = {}

    upgradeData.value = {
      data: [],
      metadata: {}
    }
  }

  return {
    list,
    country,
    getCountry,
    metadata,
    upgradeData,
    getAll,
    fetchUpsell,
    checkout,
    upgrade,
    verifyPayment,
    getOrderUpgradeList,
    getUpgradeableList,
    checkoutRetake,
    runningPromotion,
    clearRegionalPricing,
    toggleRegionalPricing,
    isPppActive,
    activeRegion,
    isLoading,
    hasUpsell,
    reset,
    parse,
    upsell
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCatalog, import.meta.hot))
}
